/// <reference lib="dom" />
import { makeAsset, type Asset, type AssetNamespace } from './Asset.js';

export interface AssetReadBackend {
  loadRaw(fullPath: string, opts?: { cache?: boolean }): Promise<{ bytes: Uint8Array; mimeType: string }>;
  /** Sync cache read (throws the preload nudge if absent); backs the sync getters. */
  peek(fullPath: string): { bytes: Uint8Array; mimeType: string; image?: unknown; url?: string };
  hasCached(fullPath: string): boolean;
}
export interface AssetWriteBackend extends AssetReadBackend {
  save(fullPath: string, bytes: Uint8Array, mimeType: string): Promise<void>;
  remove(fullPath: string): Promise<void>;
}

export interface AssetReadScope {
  load(path: string, opts?: { cache?: boolean }): Promise<Asset>;
  has(path: string): boolean;
  getJSON<T = unknown>(path: string): T;
  getFile(path: string): Uint8Array;
  getImage(path: string): HTMLImageElement;
  getUrl(path: string): string;
}
export interface AssetWriteScope extends AssetReadScope {
  save(path: string, value: unknown): Promise<void>;
  remove(path: string): Promise<void>;
}

function guard(relPath: string): string {
  if (relPath.startsWith('/') || relPath.includes('..') || relPath.includes('\\')) {
    throw new Error(`Asset path "${relPath}" is outside its namespace scope.`);
  }
  return relPath;
}

export function makeReadScope(backend: AssetReadBackend, namespace: AssetNamespace): AssetReadScope {
  const full = (relPath: string) => `${namespace}/${guard(relPath)}`;
  return {
    async load(relPath, opts) {
      const { bytes, mimeType } = await backend.loadRaw(full(relPath), opts);
      return makeAsset(namespace, guard(relPath), bytes, mimeType);
    },
    has(relPath) { return backend.hasCached(full(relPath)); },
    getJSON<T = unknown>(relPath: string): T {
      return JSON.parse(new TextDecoder().decode(backend.peek(full(relPath)).bytes)) as T;
    },
    getFile(relPath) { return backend.peek(full(relPath)).bytes; },
    getImage(relPath) {
      const a = backend.peek(full(relPath));
      if (!a.image) throw new Error(`Asset "${full(relPath)}" is not a decoded image (mime ${a.mimeType}).`);
      return a.image as HTMLImageElement;
    },
    getUrl(relPath) {
      const a = backend.peek(full(relPath));
      if (!a.url) throw new Error(`Asset "${full(relPath)}" has no URL.`);
      return a.url;
    },
  };
}

export function makeWriteScope(backend: AssetWriteBackend, namespace: AssetNamespace): AssetWriteScope {
  const read = makeReadScope(backend, namespace);
  return {
    load: read.load,
    has: read.has,
    getJSON: read.getJSON,
    getFile: read.getFile,
    getImage: read.getImage,
    getUrl: read.getUrl,
    async save(relPath, value) {
      const p = guard(relPath);
      if (value === undefined) {
        // JSON.stringify(undefined) returns `undefined` (not a string), which
        // would silently encode to an empty file; a later load().json() would
        // then throw on JSON.parse(''). Fail fast at the call site instead.
        throw new Error(`Asset save value must be JSON-serializable (got undefined).`);
      }
      const bytes = new TextEncoder().encode(JSON.stringify(value));
      await backend.save(`${namespace}/${p}`, bytes, 'application/json');
    },
    async remove(relPath) {
      const p = guard(relPath);
      await backend.remove(`${namespace}/${p}`);
    },
  };
}
